/**
 * @architecture-header 1
 * @layer module
 * @domain search
 * @responsibility Load the teacher workspace search results through the approved API boundary.
 * @state-owner TanStack Query
 * @allowed-imports app environment; TanStack Query; Zod
 * @forbidden routes; direct component API calls; Zustand server entities
 * @public-api useTeacherWorkspaceSearch, SearchEntityType, SearchResultRow,
 *   TeacherWorkspaceSearchResults, EMPTY_SEARCH_RESULTS, searchKeys -- consumed by
 *   ../../../routes/shell/SearchOverlay.tsx and CommandPalette.tsx
 * @side-effects authenticated GET requests outside demo mode
 * @naming-hook GET -> use<Resource> (no verb); no useGet/useFetch/useLoad prefixes
 * @naming-path plural lowercase nouns; organization is the active-context scope hint
 * @naming-body camelCase; never caller role or server-side identity fields
 * @tests exercised through ../../../routes/shell/CommandPalette.test.tsx and
 *   ../../../routes/shell/SearchOverlay.test.tsx; wire-shape drift locked by
 *   services/api/tests/test_search_teacher_workspace_http.py
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { useWorkspaceEnvironment } from '../../../app/config/environment';

const searchPath = '/api/v1/search';

export type SearchEntityType = 'classroom' | 'curriculum' | 'activity' | 'student';

const searchResultRowSchema = z.object({
  id: z.string(),
  title: z.string(),
  detail: z.string(),
  section: z.enum(['act', 'room', 'live', 'rep']),
});

export type SearchResultRow = z.infer<typeof searchResultRowSchema>;

const searchResponseSchema = z.object({
  classroom: z.array(searchResultRowSchema).optional(),
  curriculum: z.array(searchResultRowSchema).optional(),
  activity: z.array(searchResultRowSchema).optional(),
  student: z.array(searchResultRowSchema).optional(),
});

export interface TeacherWorkspaceSearchResults {
  readonly classroom: readonly SearchResultRow[];
  readonly curriculum: readonly SearchResultRow[];
  readonly activity: readonly SearchResultRow[];
  readonly student: readonly SearchResultRow[];
}

export const EMPTY_SEARCH_RESULTS: TeacherWorkspaceSearchResults = {
  classroom: [],
  curriculum: [],
  activity: [],
  student: [],
};

export const searchKeys = {
  teacherWorkspace: (organizationId: string, query: string, types: readonly SearchEntityType[]) =>
    ['search', 'teacher-workspace', organizationId, query, [...types].sort()] as const,
};

function matches(row: SearchResultRow, normalizedQuery: string): boolean {
  return (
    row.title.toLocaleLowerCase().includes(normalizedQuery) ||
    row.detail.toLocaleLowerCase().includes(normalizedQuery)
  );
}

function pickRequestedTypes(
  results: TeacherWorkspaceSearchResults,
  types: readonly SearchEntityType[],
  query: string,
): TeacherWorkspaceSearchResults {
  const requested = new Set(types);
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const rows = (category: SearchEntityType) =>
    requested.has(category)
      ? results[category].filter((row) => !normalizedQuery || matches(row, normalizedQuery))
      : [];
  return {
    classroom: rows('classroom'),
    curriculum: rows('curriculum'),
    activity: rows('activity'),
    student: rows('student'),
  };
}

interface TeacherWorkspaceSearchE2eFixture {
  readonly status: 'loading' | 'empty' | 'error' | 'denied' | 'ready';
  results?: TeacherWorkspaceSearchResults;
}

function fixture(): TeacherWorkspaceSearchE2eFixture | undefined {
  return (
    globalThis as typeof globalThis & {
      __VISPO_SH_F02_FIXTURE__?: TeacherWorkspaceSearchE2eFixture;
    }
  ).__VISPO_SH_F02_FIXTURE__;
}

/** Debounce the query text at the call site (see useDebouncedValue) before passing it in here. */
export function useTeacherWorkspaceSearch(
  organizationId: string | null | undefined,
  query: string,
  types: readonly SearchEntityType[],
) {
  const environment = useWorkspaceEnvironment();
  const trimmed = query.trim();
  return useQuery({
    queryKey: searchKeys.teacherWorkspace(organizationId ?? '', trimmed, types),
    enabled:
      (environment.demoMode || Boolean(organizationId)) && trimmed.length > 0 && types.length > 0,
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<TeacherWorkspaceSearchResults> => {
      if (environment.demoMode) {
        const current = fixture();
        if (current?.status === 'loading') return new Promise(() => undefined);
        if (current?.status === 'denied')
          throw Object.assign(new Error('Search access denied'), { status: 403 });
        if (current?.status === 'error') throw new Error('Search could not be loaded');
        if (current?.status === 'empty') return EMPTY_SEARCH_RESULTS;
        return pickRequestedTypes(current?.results ?? EMPTY_SEARCH_RESULTS, types, trimmed);
      }
      const params = new URLSearchParams({ organizationId: organizationId ?? '', q: trimmed });
      // Comma-separated is the CONTRACT READY wire form (?types=classroom,activity) -- SH-F02
      // round-1 rework (Sol BLOCKING finding 4). See search_teacher_workspace_types_wire_form_
      // matches_the_frontend_hook in test_search_teacher_workspace_http.py for the drift lock.
      if (types.length > 0) params.set('types', types.join(','));
      const payload = await environment.apiClient.request<unknown>(
        `${searchPath}?${params.toString()}`,
      );
      const parsed = searchResponseSchema.parse(payload);
      return {
        classroom: parsed.classroom ?? [],
        curriculum: parsed.curriculum ?? [],
        activity: parsed.activity ?? [],
        student: parsed.student ?? [],
      };
    },
    retry: 1,
  });
}
